import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Check, X, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getWarehouseReturns, confirmWarehouseReturn, cancelWarehouseReturn, getWarehouses } from '@/lib/api';
import { RETURN_CONDITIONS, fmtDT } from '@/components/warehouse/ReturnsSection';

const SEEN_KEY = 'pharmx_seen_return_ids';
const MONO = "'IBM Plex Mono', ui-monospace, monospace";

const getSeen = () => { try { return JSON.parse(localStorage.getItem(SEEN_KEY)) || null; } catch { return null; } };
const saveSeen = (ids) => localStorage.setItem(SEEN_KEY, JSON.stringify(ids.slice(-300)));

export const ReturnAlertPopup = () => {
  const [fresh, setFresh] = useState([]);
  const [open, setOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [whFor, setWhFor] = useState({});
  const [condFor, setCondFor] = useState({});
  const [busy, setBusy] = useState(null);
  const timer = useRef(null);

  const check = useCallback(async () => {
    try {
      const { data } = await getWarehouseReturns({ status: 'pending' });
      const pending = data || [];
      const seen = getSeen();
      if (seen === null) { saveSeen(pending.map(r => r.return_id)); return; }
      const newOnes = pending.filter(r => !seen.includes(r.return_id));
      if (newOnes.length > 0) {
        saveSeen([...seen, ...newOnes.map(r => r.return_id)]);
        setFresh(prev => [...prev, ...newOnes.filter(n => !prev.some(p => p.return_id === n.return_id))]);
        setOpen(true);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    getWarehouses().then(({ data }) => setWarehouses(data || [])).catch(() => {});
    check();
    timer.current = setInterval(check, 45000);
    return () => clearInterval(timer.current);
  }, [check]);

  const done = (id) => {
    setFresh(prev => {
      const next = prev.filter(r => r.return_id !== id);
      if (next.length === 0) setOpen(false);
      return next;
    });
    window.dispatchEvent(new CustomEvent('pharmx-returns-changed'));
  };

  const confirm = async (r) => {
    const wid = whFor[r.return_id] || warehouses[0]?.warehouse_id;
    if (!wid) { toast.error('Выберите склад'); return; }
    setBusy(r.return_id);
    try { await confirmWarehouseReturn(r.return_id, wid, condFor[r.return_id] || 'good'); toast.success('✅ Возврат принят · склад обновлён'); done(r.return_id); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Ошибка'); }
    finally { setBusy(null); }
  };

  const reject = async (r) => {
    setBusy(r.return_id);
    try { await cancelWarehouseReturn(r.return_id, ''); toast.success('Возврат отклонён'); done(r.return_id); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Ошибка'); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
      <DialogContent className="max-w-md" data-testid="return-alert-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <RotateCcw className="w-4 h-4 text-amber-600" />
            Новый возврат на склад
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{fresh.length}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          {fresh.map(r => (
            <div key={r.return_id} className="border p-3 space-y-1.5" style={{ borderRadius: 12 }} data-testid={`popup-return-${r.return_id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[13px] font-bold">{r.apteka_name}</p>
                <span className="text-[10px] text-muted-foreground tabular-nums shrink-0" data-testid={`popup-return-date-${r.return_id}`}>📅 {fmtDT(r.created_at)}</span>
              </div>
              <p className="text-[12px]" style={{ fontFamily: MONO }}>{r.product_name} × <b>{r.qty}</b> шт</p>
              <p className="text-[10.5px] text-muted-foreground">📝 {r.reason} · 👤 {r.requested_by_name}{r.month ? ` · ${r.month}` : ''}</p>
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                {warehouses.length > 1 && (
                  <select value={whFor[r.return_id] || warehouses[0]?.warehouse_id || ''}
                    onChange={e => setWhFor(p => ({ ...p, [r.return_id]: e.target.value }))}
                    className="h-8 px-2 rounded-lg border bg-card text-[11px] flex-1 min-w-[110px]"
                    data-testid={`popup-warehouse-select-${r.return_id}`}>
                    {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>)}
                  </select>
                )}
                <select value={condFor[r.return_id] || 'good'}
                  onChange={e => setCondFor(p => ({ ...p, [r.return_id]: e.target.value }))}
                  className="h-8 px-2 rounded-lg border bg-card text-[11px] min-w-[100px]"
                  data-testid={`popup-condition-select-${r.return_id}`}>
                  {RETURN_CONDITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button onClick={() => confirm(r)} disabled={busy === r.return_id}
                  className="h-8 px-3 rounded-lg text-[11px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-50"
                  style={{ background: '#1f9d6c' }} data-testid={`popup-confirm-${r.return_id}`}>
                  {busy === r.return_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Принято
                </button>
                <button onClick={() => reject(r)} disabled={busy === r.return_id}
                  className="h-8 px-3 rounded-lg text-[11px] font-bold border text-rose-600 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid={`popup-reject-${r.return_id}`}>
                  <X className="w-3.5 h-3.5" /> Отклонить
                </button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => setOpen(false)} className="text-[11px] underline text-muted-foreground mx-auto" data-testid="popup-later-btn">
          Позже — посмотрю в Warehouse · Orders
        </button>
      </DialogContent>
    </Dialog>
  );
};
